import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Send, CheckCircle2, Clock, RotateCcw, SkipForward, Pencil, Check, X, Sparkles, MessageSquare } from "lucide-react";
import { listSessionAgreements, createSessionAgreement, updateSessionAgreement, deleteSessionAgreement, publishSessionAgreements } from "../../../application/sessionAgreements";
import type { SessionAgreement } from "../../../domain/Session";
import Button from "../../../../../shared/ui/button/Button";

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  in_progress: "bg-accent-tint text-accent-text",
  completed: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400",
  skipped: "bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400",
};

const STATUS_ICONS: Record<string, JSX.Element> = {
  pending: <Clock className="w-3 h-3" />,
  in_progress: <RotateCcw className="w-3 h-3" />,
  completed: <CheckCircle2 className="w-3 h-3" />,
  skipped: <SkipForward className="w-3 h-3" />,
};

interface Props {
  sessionId: string;
}

export default function SessionAgreementsTab({ sessionId }: Props) {
  const { t } = useTranslation();
  const [agreements, setAgreements] = useState<SessionAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    listSessionAgreements(sessionId)
      .then((data) => setAgreements(data))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const handleAdd = async () => {
    const text = newText.trim();
    if (!text) return;
    setAdding(true);
    try {
      const data = await createSessionAgreement(sessionId, text);
      setAgreements(prev => [...prev, data]);
      setNewText("");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteSessionAgreement(id);
    setAgreements(prev => prev.filter(a => a.id !== id));
  };

  const startEdit = (a: SessionAgreement) => {
    setEditingId(a.id);
    setEditingText(a.text);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const saveEdit = async (id: string) => {
    const text = editingText.trim();
    if (!text) { setEditingId(null); return; }
    const data = await updateSessionAgreement(id, { text });
    setAgreements(prev => prev.map(a => a.id === id ? data : a));
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const handlePublish = async () => {
    const hasDrafts = agreements.some(a => a.state === "draft");
    if (!hasDrafts) return;
    if (!window.confirm(t("session.agreements.publishConfirm"))) return;
    setPublishing(true);
    try {
      const data = await publishSessionAgreements(sessionId);
      setAgreements(data);
    } finally {
      setPublishing(false);
    }
  };

  const drafts = agreements.filter(a => a.state === "draft");
  const published = agreements.filter(a => a.state === "published");

  if (loading) return (
    <div className="flex justify-center py-16 text-content-tertiary text-sm">{t("common.loading")}</div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">
            {t("session.agreements.title")}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{t("session.agreements.subtitle")}</p>
        </div>
        <Button
          size="sm"
          onClick={handlePublish}
          disabled={drafts.length === 0 || publishing}
          loading={publishing}
          startIcon={<Send className="w-3.5 h-3.5" />}
        >
          {t("session.agreements.publish")}
        </Button>
      </div>

      {/* Draft agreements */}
      {drafts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-content-tertiary uppercase tracking-wider px-1">
            {t("session.agreements.drafts")} ({drafts.length})
          </p>
          <ul className="space-y-2">
            {drafts.map(a => (
              <li
                key={a.id}
                className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.02] group"
              >
                <div className="flex-1 min-w-0">
                  {editingId === a.id ? (
                    <div className="flex items-start gap-2">
                      <textarea
                        ref={inputRef}
                        value={editingText}
                        onChange={e => setEditingText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) saveEdit(a.id);
                          if (e.key === "Escape") cancelEdit();
                        }}
                        rows={3}
                        className="flex-1 text-sm bg-transparent border-b border-brand-400 outline-none text-content-primary py-0.5 resize-none"
                      />
                      <button onClick={() => saveEdit(a.id)} className="text-green-500 hover:text-green-600 mt-0.5">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEdit} className="text-content-tertiary hover:text-gray-600 mt-0.5">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-gray-800 dark:text-gray-200 flex-1">{a.text}</p>
                      {a.source === "ai" && (
                        <span title={t("session.agreements.aiGenerated")} className="shrink-0 mt-0.5">
                          <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {editingId !== a.id && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={() => startEdit(a)}
                      className="p-1 rounded-lg text-content-tertiary hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(a.id)}
                      className="p-1 rounded-lg text-content-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add new */}
      <div className="flex gap-2 items-start">
        <textarea
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAdd(); }}
          placeholder={t("session.agreements.addPlaceholder")}
          rows={3}
          className="field-base field-default flex-1 resize-none px-4 py-2.5"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleAdd}
          disabled={!newText.trim() || adding}
          loading={adding}
          startIcon={<Plus className="w-3.5 h-3.5" />}
        >
          {t("session.agreements.add")}
        </Button>
      </div>

      {/* Published agreements */}
      {published.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-content-tertiary uppercase tracking-wider px-1">
            {t("session.agreements.published")} ({published.length})
          </p>
          <ul className="space-y-2">
            {published.map(a => (
              <li
                key={a.id}
                className="flex flex-col gap-2 p-3 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.01]"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-content-secondary flex-1">{a.text}</p>
                      {a.source === "ai" && (
                        <span title={t("session.agreements.aiGenerated")} className="shrink-0 mt-0.5">
                          <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                        </span>
                      )}
                    </div>
                    {a.published_at && (
                      <p className="text-xs text-content-tertiary mt-1">
                        {t("session.agreements.publishedOn")} {new Date(a.published_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {a.status && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${STATUS_STYLES[a.status] ?? STATUS_STYLES.pending}`}>
                      {STATUS_ICONS[a.status]}
                      {t(`session.agreements.status.${a.status}`, a.status)}
                    </span>
                  )}
                </div>
                {(a.patient_comments?.length ?? 0) > 0 && (
                  <div className="space-y-1.5">
                    {a.patient_comments!.map((comment, i) => (
                      <div key={i} className="flex items-start gap-2 rounded-lg bg-accent-tint border border-accent/20 px-3 py-2">
                        <MessageSquare className="w-3.5 h-3.5 text-accent-text mt-0.5 shrink-0" />
                        <p className="text-xs text-accent-text leading-relaxed">{comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {agreements.length === 0 && (
        <p className="text-center text-sm text-content-tertiary py-8">{t("session.agreements.empty")}</p>
      )}
    </div>
  );
}
